import React, { useState } from 'react'
import { useNavigate, NavLink } from 'react-router-dom';

import { loginUser } from '../../apis/Authentication';
import { useDispatch, useSelector } from 'react-redux';
import { setUserInfo, setUserToken, setLoginState, setSandBoxNumber, 
    setApiKey, setCallbackUrl, setStatusCallbackUrl } from '../../redux-store/AuthSlice';
import { Toaster, toast } from 'sonner'

const Login = () => {

    const dispatch = useDispatch()
    const loginPending = useSelector((state) => state.action)
    let navigate = useNavigate()

    const [user, setUser] = useState()
    const [formErrors, setFormErrors] = useState()

    // Handling setting user info
    const handleOnChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value })
    }

    const loading = () => {
        return (
            <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent border-r-transparent rounded-full animate-spin"></div>
        )
    }

    // Handling form validation
    const validate = () => {
        let errors = {}

        if (!user?.username) {
            errors['username'] = 'Username is required!'
        }
        if (!user?.password) {
            errors['password'] = 'Password is required!'
        }
        setFormErrors(errors)
        return errors
    };

    // Handle: User login
    const handleLoginUser = () => {
        if (Object.keys(validate(user)).length === 0) {
            loginUser(dispatch, user).then((resp) => {
                if (resp?.status === 200) {
                    localStorage.setItem("token", resp?.data?.data?.token)
                    localStorage.setItem("userinfo", JSON.stringify(resp?.data?.data?.user))
                    localStorage.setItem("loginState", true)
                    localStorage.setItem("apikey", JSON.stringify(resp?.data?.data?.user?.client?.apiKey))
                    localStorage.setItem("callbackUrl", JSON.stringify(resp?.data?.data?.user?.client?.callbackUrl))
                    localStorage.setItem("statusCallbackUrl", JSON.stringify(resp?.data?.data?.user?.client?.statusCallbackUrl))
                    localStorage.setItem("sandBoxNumber", resp?.data?.data?.config?.sandboxNumber)
                    dispatch(setUserToken(localStorage.getItem("token")))
                    dispatch(setUserInfo(localStorage.getItem("userinfo")))
                    dispatch(setLoginState(localStorage.getItem("loginState")))
                    dispatch(setSandBoxNumber(localStorage.getItem("sandBoxNumber")))
                    dispatch(setApiKey(localStorage.getItem("apikey")))
                    dispatch(setCallbackUrl(localStorage.getItem("callbackUrl")))
                    dispatch(setStatusCallbackUrl(localStorage.getItem("statusCallbackUrl")))
                    navigate('/app/home')
                }
                else if (resp?.response?.data?.message?.includes("User does not exists") || resp?.response?.status === 401) {
                    toast.error('Invalid email address or password!')
                }
                else {
                    toast.error(`Unexpected error occurred, please try again!`)
                }
            })
        }
    }

    return (
        <div>
            <Toaster position="top-right" expand={false} richColors/>

            <div className='min-h-screen flex items-center justify-center overflow-y-auto'>
                
                {/* Left Side - Hidden on mobile */}
                <div className='hidden md:flex w-1/2 min-h-screen items-center bg-gradient-to-br from-red-500 to-sky-500 px-4 lg:px-8'>
                    <div className='w-full max-w-lg mx-auto px-2 lg:px-4'>
                        <img 
                            src={process.env.PUBLIC_URL + "/images/logo.png"} 
                            alt="welcome-logo" 
                            className="w-24 lg:w-30 mb-4" 
                        />
                        <h1 className='text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4'>
                            Welcome Back
                        </h1>
                        <p className='text-base lg:text-lg xl:text-xl text-white/80 mb-6'>
                            Connect with your customers seamlessly through our powerful WhatsApp API gateway.
                        </p>
                        <ul className='space-y-2 lg:space-y-3 mb-8'>
                            <li className='text-sm lg:text-base font-semibold text-white/80'>
                                Send messages, notifications, and updates seamlessly
                            </li>
                            <li className='text-sm lg:text-base font-semibold text-white/80'>
                                Build bots to automate customer service and marketing
                            </li>
                            <li className='text-sm lg:text-base font-semibold text-white/80'>
                                Send broadcasts to all your contacts in minutes
                            </li>
                            <li className='text-sm lg:text-base font-semibold text-white/80'>
                                Manage your contacts
                            </li>
                            <li className='text-sm lg:text-base font-semibold text-white/80'>
                                Save money by saving time
                            </li>
                        </ul>
                        <a 
                            href='https://docs.lipachat.com/getting-started/quickstart' 
                            target="_blank" 
                            rel="noreferrer"
                            className='inline-block bg-transparent border border-indigo-200 text-white px-4 lg:px-5 py-2 lg:py-3 rounded-2xl text-sm lg:text-base font-bold hover:bg-white hover:text-sky-500 transition-colors duration-200'
                        >
                            Documentation
                        </a>
                    </div>
                </div>

                {/* Right Side - Form Section */}
                <div className='w-full md:w-1/2 bg-white flex justify-center items-center min-h-screen p-4 sm:p-2 md:p-2 lg:p-12'>
                    <form className='w-full max-w-sm lg:max-w-md xl:max-w-lg p-4 sm:min-w-[100%] lg:p-6'>
                        <h2 className='text-lg lg:text-xl xl:text-2xl font-bold text-red-500 mb-6 lg:mb-8'>
                            Sign in to your account
                        </h2>

                        <div className='flex flex-col mb-4'>
                            <label className='text-gray-700 text-sm font-semibold mb-1'>
                                Email Address
                            </label>
                            <input 
                                type="email" 
                                name='username' 
                                className='px-2 py-2 rounded rounded-[7px] text-sm border border-gray-300 rounded focus:outline-none focus:border-sky-500 font-medium' 
                                placeholder='email@domain.com' 
                                onChange={(e) => handleOnChange(e)} 
                            />
                            <span className='text-red-500 text-xs mt-1'>
                                {formErrors?.username}
                            </span>
                        </div>

                        <div className='flex flex-col mb-2'>
                            <label className='text-gray-700 text-sm font-semibold mb-1'>
                                Password
                            </label>
                            <input 
                                type="password" 
                                name='password' 
                                className='px-2 py-2 rounded rounded-[7px] text-sm border border-gray-300 rounded focus:outline-none focus:border-sky-500 font-medium' 
                                onChange={(e) => handleOnChange(e)} 
                            />
                            <span className='text-red-500 text-xs mt-1'>
                                {formErrors?.password}
                            </span>
                        </div>

                        <NavLink 
                            to="/auth/forgot_pass" 
                            className="text-blue-700 text-sm no-underline block mb-4"
                        >
                            Forgot Password?
                        </NavLink>

                        <div className='mb-4 lg:mb-6'>
                            {!loginPending.pending ? (
                                <button 
                                    type='button' 
                                    className="w-full bg-sky-500 text-white py-2 lg:py-3 text-sm font-bold rounded cursor-pointer hover:opacity-70 transition-opacity duration-300"
                                    onClick={handleLoginUser}
                                >
                                    Login
                                </button>
                            ) : (
                                <button 
                                    type='button' 
                                    className="w-full bg-sky-500 opacity-70 text-white py-2 lg:py-3 rounded cursor-not-allowed flex justify-center items-center"
                                    disabled={true}
                                >
                                    {loading()}
                                </button>
                            )}
                        </div>

                        <div className='text-center'>
                            <p className="text-sm font-semibold">
                                Do not have an account?{' '}
                                <NavLink 
                                    to="/auth/signup" 
                                    className="font-semibold no-underline text-blue-700"
                                >
                                    Sign Up
                                </NavLink>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login